<template>
  <div class="server-enrolment-modal__backdrop" @click.self="$emit('close')">
    <div class="server-enrolment-modal">
      <div class="server-enrolment-modal__header">
        <h3>{{ adoptTarget ? `Adopt ${adoptTarget.hostname}` : 'Enrol a server' }}</h3>
        <button type="button" class="server-enrolment-modal__close" @click="$emit('close')">&times;</button>
      </div>

      <div class="server-enrolment-modal__body">
        <p class="server-enrolment-modal__intro">
          <template v-if="adoptTarget">
            Already reported by a connected provider, no agent yet — running the enrol command below binds the agent to this
            existing row instead of creating a new one.
          </template>
          <template v-else>
            Generate a token and run the enrol command on a box Goshawk can't see yet.
          </template>
        </p>

        <div v-if="error" class="server-enrolment-modal__error">{{ error }}</div>

        <div class="server-enrolment-modal__step">
          <div class="server-enrolment-modal__step-label"><span class="server-enrolment-modal__step-num">1</span>{{ adoptTarget ? 'Generate a pre-filled token' : 'Generate an enrolment token' }}</div>

          <button
            v-if="!token"
            type="button"
            class="server-enrolment-modal__btn server-enrolment-modal__btn--primary"
            :disabled="generating"
            @click="generateToken"
          >{{ generating ? 'Generating…' : 'Generate token' }}</button>

          <div v-else class="server-enrolment-modal__token-row">
            <code>{{ token }}</code>
            <span class="server-enrolment-modal__token-expiry">expires {{ expiryLabel }}</span>
            <button type="button" class="server-enrolment-modal__copy" @click="copy(token)">Copy</button>
          </div>
        </div>

        <div v-if="token" class="server-enrolment-modal__step">
          <div class="server-enrolment-modal__step-label"><span class="server-enrolment-modal__step-num">2</span>Run the enrol command on the server</div>
          <div class="server-enrolment-modal__install-cmd">
            <code>{{ enrolCommand }}</code>
            <button type="button" class="server-enrolment-modal__copy server-enrolment-modal__copy--dark" @click="copy(enrolCommand)">Copy</button>
          </div>
          <p class="server-enrolment-modal__cmd-note">
            Needs the Luna CLI on the box already — there's no public bootstrap installer yet.
            <template v-if="!hubWssUrl">
              No hub is registered on this install, so swap the <code>wss://&lt;hub-host&gt;:8765</code> placeholder for
              your hub's WSS endpoint.
            </template>
            <code>--insecure</code> covers first contact only: the agent pins the hub's certificate during enrolment and
            verifies it from then on.
          </p>
        </div>

        <div v-if="token" class="server-enrolment-modal__step">
          <div class="server-enrolment-modal__step-label"><span class="server-enrolment-modal__step-num">3</span>Enrolment progress</div>
          <div class="server-enrolment-modal__stepper">
            <div class="server-enrolment-modal__stage server-enrolment-modal__stage--done">
              <div class="server-enrolment-modal__node">&check;</div>
              <div class="server-enrolment-modal__lbl">Token generated</div>
            </div>
            <div
              class="server-enrolment-modal__stage"
              :class="progress === 'enrolled' ? 'server-enrolment-modal__stage--done' : 'server-enrolment-modal__stage--current'"
            >
              <div class="server-enrolment-modal__node">2</div>
              <div class="server-enrolment-modal__lbl">Waiting for check-in</div>
            </div>
            <div class="server-enrolment-modal__stage" :class="{ 'server-enrolment-modal__stage--current': progress === 'enrolled' }">
              <div class="server-enrolment-modal__node">3</div>
              <div class="server-enrolment-modal__lbl">Enrolled</div>
            </div>
          </div>

          <p v-if="adoptTarget && progress !== 'enrolled'" class="server-enrolment-modal__poll-note">
            Watching <code>{{ adoptTarget.hostname }}</code> for its first check-in…
          </p>
          <p v-else-if="!adoptTarget" class="server-enrolment-modal__poll-note">
            A manual token isn't pre-linked to a row, so there's nothing to poll yet — close this dialog once the agent has
            checked in and refresh the fleet list to see it.
          </p>
          <p v-else class="server-enrolment-modal__poll-note server-enrolment-modal__poll-note--done">
            Enrolled — closing shortly.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';

// Token-generation modal behind BOTH `enrolment-flow.html` paths — manual
// (no `adoptTarget`) and adopt (pre-linked `adoptTarget` row, POSTs
// `/adopt` instead of `/enrolment-tokens` and polls that specific server's
// `agent_status` for the Enrolling -> Healthy transition). See
// `EnrolmentController::adopt()`/`::create()` for what each path returns.
const props = defineProps({
  apiBase: {
    type: String,
    default: '/api/v1/servers',
  },
  hub: {
    type: Object,
    default: null,
  },
  adoptTarget: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'enrolled']);

const api = useApi();

const token = ref(null);
const expiresAt = ref(null);
const generating = ref(false);
const error = ref(null);
const progress = ref('waiting');
let pollHandle = null;

const expiryLabel = computed(() => {
  if (!expiresAt.value) {
    return '';
  }
  const seconds = Math.max(0, expiresAt.value - Math.floor(Date.now() / 1000));
  return seconds > 60 ? `in ${Math.round(seconds / 60)} min` : `in ${seconds}s`;
});

// The minted token feeds `luna agent enroll` directly. This used to render
// `curl {origin}/install | bash -s -- --token=...`, but the bootstrap
// endpoint behind it never existed (backlog: "Agent bootstrap endpoint")
// and `{origin}` fell back to the SPA origin — so the command piped the SPA
// catch-all's HTML into bash. Until a real bootstrap ships, be honest about
// what exists today: the Luna CLI must already be on the box, and the
// command that consumes this token is
// `luna agent enroll --hub <wss-url> --token <token>` (luna-node
// `src/commands/agent/index.ts`). `--hub` is the HUB's WSS endpoint —
// never the Goshawk API or SPA origin.
const hubWssUrl = computed(() => {
  const raw = props.hub && props.hub.wss_host ? String(props.hub.wss_host).trim() : '';
  if (!raw) {
    return null;
  }
  // Same guard as `AdoptRequestController::normaliseHubUrl()` — a bare
  // `host:port` makes the agent's `new URL()` parse the port as a scheme
  // (broke the first live self-adopt on demeter, 2026-07-18).
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `wss://${raw.replace(/\/+$/, '')}`;
});

const enrolCommand = computed(
  () => `sudo luna agent enroll --hub ${hubWssUrl.value || 'wss://<hub-host>:8765'} --token ${token.value} --insecure`
);

async function generateToken() {
  generating.value = true;
  error.value = null;
  try {
    if (props.adoptTarget) {
      const data = await api.post(`${props.apiBase}/${props.adoptTarget.id}/adopt`);
      token.value = data.token;
      expiresAt.value = data.expires_at;
      startPolling();
    } else {
      const data = await api.post(`${props.apiBase}/enrolment-tokens`);
      token.value = data.token;
      expiresAt.value = data.expires_at;
    }
  } catch (err) {
    error.value = err?.response?.data?.error || 'Could not generate a token.';
  } finally {
    generating.value = false;
  }
}

function startPolling() {
  if (!props.adoptTarget) {
    return;
  }
  pollHandle = setInterval(async () => {
    try {
      const data = await api.get(`${props.apiBase}/${props.adoptTarget.id}`);
      if (data?.server?.agent_status && data.server.agent_status !== 'enrolling' && data.server.agent_status !== 'unmanaged') {
        progress.value = 'enrolled';
        clearInterval(pollHandle);
        emit('enrolled');
        setTimeout(() => emit('close'), 1500);
      }
    } catch {
      // Best-effort — a transient failure just tries again next tick.
    }
  }, 4000);
}

function copy(text) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {});
  }
}

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
});

// Adopt path pre-fills immediately — the row is already known, so token
// generation can start right away instead of waiting for a click when the
// modal is opened from a specific "Install agent" row action.
if (props.adoptTarget) {
  generateToken();
}
</script>

<style lang="scss">
.server-enrolment-modal {
  background: var(--theme-bg-card);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-panel);
  width: min(560px, 92vw);
  max-height: 88vh;
  overflow-y: auto;

  &__backdrop {
    position: fixed;
    inset: 0;
    background: var(--theme-bg-overlay, rgba(14, 46, 48, 0.65));
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 6vh 16px;
    z-index: 1000;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--theme-border-soft);

    h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
    }
  }

  &__close {
    border: none;
    background: none;
    font-size: 20px;
    line-height: 1;
    color: var(--theme-text-tertiary);
    cursor: pointer;
  }

  &__body {
    padding: 16px 20px 20px;
  }

  &__intro {
    font-size: 12.5px;
    color: var(--theme-text-secondary);
    margin: 0 0 16px;
    line-height: 1.55;
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 8px 12px;
    font-size: 12px;
    margin-bottom: 14px;
  }

  &__step {
    margin-top: 16px;

    &:first-of-type {
      margin-top: 0;
    }
  }

  &__step-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    margin-bottom: 8px;
  }

  &__step-num {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }

  &__token-row, &__install-cmd {
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
  }

  &__token-row {
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border);

    code {
      flex: 1;
      font-family: var(--theme-font-mono);
      font-size: 12.5px;
    }
  }

  &__token-expiry {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    white-space: nowrap;
  }

  &__install-cmd {
    background: var(--theme-bg-sidebar);

    code {
      flex: 1;
      font-family: var(--theme-font-mono);
      font-size: 12px;
      color: var(--theme-text-sidebar, #b9cbca);
      overflow-x: auto;
      white-space: nowrap;
    }
  }

  &__copy {
    font-size: 11.5px;
    font-weight: 600;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    cursor: pointer;
    white-space: nowrap;

    &--dark {
      background: rgba(255, 255, 255, .08);
      border-color: var(--theme-border-dark);
      color: var(--theme-text-sidebar, #b9cbca);
    }
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__stepper {
    display: flex;
    align-items: center;
    margin: 4px 0;
  }

  &__stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;

    &:not(:last-child)::after {
      content: "";
      position: absolute;
      top: 10px;
      left: calc(50% + 11px);
      width: calc(100% - 22px);
      height: 2px;
      background: var(--theme-border-strong);
    }

    &--done:not(:last-child)::after {
      background: var(--theme-status-success);
    }
  }

  &__node {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    border: 2px solid var(--theme-border-strong);
    color: var(--theme-text-tertiary);
    background: var(--theme-bg-card);
    z-index: 1;

    .server-enrolment-modal__stage--done & {
      border-color: var(--theme-status-success);
      background: var(--theme-status-success);
      color: #fff;
    }

    .server-enrolment-modal__stage--current & {
      border-color: var(--theme-accent-primary);
      color: var(--theme-accent-dark);
      background: var(--theme-accent-light);
    }
  }

  &__lbl {
    margin-top: 6px;
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    text-align: center;

    .server-enrolment-modal__stage--current & {
      color: var(--theme-accent-dark);
      font-weight: 700;
    }

    .server-enrolment-modal__stage--done & {
      color: #1c7a4d;
      font-weight: 600;
    }
  }

  &__cmd-note {
    font-size: 11.5px;
    line-height: 1.5;
    color: var(--theme-text-tertiary);
    margin: 8px 0 0;

    code {
      font-family: var(--theme-font-mono);
    }
  }

  &__poll-note {
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin: 10px 0 0;

    code {
      font-family: var(--theme-font-mono);
    }

    &--done {
      color: #1c7a4d;
      font-weight: 600;
    }
  }
}
</style>
